import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import adminSchemaApi from '../api/adminSchemaApi';
import { fieldMigrationObjectsForDisplay } from './AdminPlanConfigConstants';

const FIELD_DELTA_TELECMI_TABLE = 'telecmi_setting';
const FIELD_DELTA_TELECMI_COL_PREFIX = '__tc_col__:';
const FIELD_DELTA_TELECMI_SAFE_PREFIX = '__tc_safe__:';
/** CRM user object vs PostgreSQL "user" table — use pseudo key for DDL/value drift on the table. */
const FIELD_DELTA_USER_DB = 'user_db';
const FIELD_DELTA_USER_FIELD_PREFIX = '__ut_field__:';
/** @deprecated legacy keys — still parsed on migrate if present in selection state */
const FIELD_DELTA_USER_COL_PREFIX = '__ut_col__:';
const FIELD_DELTA_USER_CRM_PREFIX = '__ut_crm__:';
const FIELD_DELTA_USER_SAFE_PREFIX = '__ut_safe__:';
const USER_CRM_OBJECT = 'user';
/** UI label for the telecmi_setting row (not from object_config). */
const TELECMI_SETTING_UI_LABEL = 'TeleCMI settings';
const USER_DB_UI_LABEL = 'User (database table)';

/** Show metadata as "Label (api_name)" when both differ; otherwise a single string. */
function labelApiDisplay(label, apiName) {
  const L = label != null && String(label).trim() !== '' ? String(label).trim() : '';
  const A = apiName != null && String(apiName).trim() !== '' ? String(apiName).trim() : '';
  if (L && A) {
    return L.toLowerCase() === A.toLowerCase() ? A : `${L} (${A})`;
  }
  return A || L || '—';
}

function userMigratableFieldActionLabel(f) {
  if (f?.configOnly && f?.needsConfig && !f?.needsColumn) {
    return 'Copies object_config_fields.config from source';
  }
  if (f?.needsColumn && f?.needsConfig) {
    return 'Adds DB column and CRM field definition (incl. config)';
  }
  if (f?.needsColumn) {
    return 'Adds DB column; copies non-sensitive values for selected user row when applicable';
  }
  if (f?.needsConfig) {
    return 'Adds CRM field definition (DB column already on target)';
  }
  return '—';
}

function userMigratableFieldDetail(f) {
  if (f?.configOnly && f?.needsConfig && !f?.needsColumn) {
    return 'Missing CRM field config (object_config_fields.config) on target';
  }
  if (f?.needsColumn && f?.needsConfig) {
    return 'Missing DB column and CRM definition on target';
  }
  if (f?.needsColumn) {
    return 'Missing DB column on target';
  }
  if (f?.needsConfig) {
    return 'Missing CRM field definition on target';
  }
  return '—';
}

/**
 * Field delta / TeleCMI compare in a modal: one table (object) at a time via dropdown.
 */
export default function AdminFieldMigrationModal({ show, onHide, company }) {
  const companyId = company?.id;
  const [migrationSchemas, setMigrationSchemas] = useState([]);
  const [migrationSourceSchema, setMigrationSourceSchema] = useState('');
  const [migrationPlan, setMigrationPlan] = useState(null);
  const [migrationPlanLoading, setMigrationPlanLoading] = useState(false);
  const [migrationError, setMigrationError] = useState('');
  const [migrationSuccess, setMigrationSuccess] = useState(null);

  const [selectedTable, setSelectedTable] = useState('');
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [selectedFields, setSelectedFields] = useState({});
  const [includeLookup, setIncludeLookup] = useState(true);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const selectedFieldsRef = useRef({});

  useEffect(() => {
    selectedFieldsRef.current = selectedFields;
  }, [selectedFields]);

  const applySelection = useCallback((updater) => {
    setSelectedFields((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      selectedFieldsRef.current = next;
      return next;
    });
  }, []);

  const loadMigrationSchemas = useCallback(async () => {
    try {
      const list = await adminSchemaApi.fetchMigrationSchemas();
      setMigrationSchemas(Array.isArray(list) ? list : []);
    } catch {
      setMigrationSchemas([]);
    }
  }, []);

  const refreshPlanForSource = useCallback(
    async (sourceSchema) => {
      if (!companyId || !sourceSchema) {
        setMigrationPlan(null);
        return;
      }
      setMigrationPlanLoading(true);
      setMigrationError('');
      try {
        const plan = await adminSchemaApi.fetchMigrationPlan(companyId, sourceSchema);
        setMigrationPlan(plan);
        if (plan?.message) setMigrationError(plan.message);
      } catch (err) {
        setMigrationError(err?.message || 'Failed to load migration plan');
        setMigrationPlan(null);
      } finally {
        setMigrationPlanLoading(false);
      }
    },
    [companyId]
  );

  useEffect(() => {
    if (!show || !companyId) return;
    loadMigrationSchemas();
    const src = company?.sourceschema ? String(company.sourceschema).trim() : '';
    setMigrationSourceSchema(src);
    setSelectedTable('');
    setCompareData(null);
    applySelection({});
    setMigrationError('');
    setMigrationSuccess(null);
  }, [show, companyId, company?.sourceschema, loadMigrationSchemas, applySelection]);

  useEffect(() => {
    if (!show || !companyId) return;
    refreshPlanForSource(migrationSourceSchema);
  }, [show, companyId, migrationSourceSchema, refreshPlanForSource]);

  const handleSourceChange = (sourceSchema) => {
    setMigrationSourceSchema(sourceSchema);
    setCompareData(null);
    applySelection({});
    setSelectedTable('');
    setMigrationSuccess(null);
  };

  const handleTableChange = (e) => {
    const v = e.target.value;
    setSelectedTable(v);
    setCompareData(null);
    applySelection({});
    setMigrationSuccess(null);
    setMigrationError('');
  };

  const runCompare = useCallback(async () => {
    if (!companyId || !migrationSourceSchema || !selectedTable) return;
    setCompareLoading(true);
    setMigrationError('');
    try {
      const data = await adminSchemaApi.compareMigrationFieldDelta(
        companyId,
        migrationSourceSchema,
        [],
        selectedTable,
        null
      );
      setCompareData(data);
      applySelection({});
      if (data?.message) setMigrationError(data.message);
    } catch (err) {
      setMigrationError(err?.message || 'Compare failed');
      setCompareData(null);
    } finally {
      setCompareLoading(false);
    }
  }, [companyId, migrationSourceSchema, selectedTable, applySelection]);

  useEffect(() => {
    if (!show || !companyId || !migrationSourceSchema || !selectedTable) return;
    runCompare();
  }, [show, companyId, migrationSourceSchema, selectedTable, company?.default_schema_user_id, runCompare]);

  const handleMigrate = useCallback(async () => {
    const selection = selectedFieldsRef.current || {};
    const keys = Object.keys(selection).filter((k) => selection[k]);
    if (!companyId || !migrationSourceSchema) {
      setMigrationError('Company or source schema is missing.');
      return;
    }
    if (migrationPlan?.targetSchemaExists && !company?.default_schema_user_id) {
      setMigrationError('Set "Default admin for schema" on the company detail page (required for audit fields on the target).');
      return;
    }
    if (keys.length === 0) {
      setMigrationError('Select at least one row, then click Migrate.');
      return;
    }
    const telecmiPrefix = `${FIELD_DELTA_TELECMI_TABLE}|||`;
    const userDbPrefix = `${FIELD_DELTA_USER_DB}|||`;
    const telecmiSyncSafeColumns = [];
    const userTableSyncSafeColumns = [];
    const byObj = {};
    keys.forEach((k) => {
      if (k.startsWith(telecmiPrefix)) {
        const rest = k.slice(telecmiPrefix.length);
        if (rest.startsWith(FIELD_DELTA_TELECMI_SAFE_PREFIX)) {
          const col = rest.slice(FIELD_DELTA_TELECMI_SAFE_PREFIX.length);
          if (col) telecmiSyncSafeColumns.push(col);
          return;
        }
        if (rest.startsWith(FIELD_DELTA_TELECMI_COL_PREFIX)) {
          const col = rest.slice(FIELD_DELTA_TELECMI_COL_PREFIX.length);
          if (col) {
            if (!byObj[FIELD_DELTA_TELECMI_TABLE]) byObj[FIELD_DELTA_TELECMI_TABLE] = [];
            byObj[FIELD_DELTA_TELECMI_TABLE].push(col);
          }
        }
        return;
      }
      if (k.startsWith(userDbPrefix)) {
        const rest = k.slice(userDbPrefix.length);
        if (rest.startsWith(FIELD_DELTA_USER_SAFE_PREFIX)) {
          const col = rest.slice(FIELD_DELTA_USER_SAFE_PREFIX.length);
          if (col) userTableSyncSafeColumns.push(col);
          return;
        }
        if (rest.startsWith(FIELD_DELTA_USER_FIELD_PREFIX)) {
          const fn = rest.slice(FIELD_DELTA_USER_FIELD_PREFIX.length);
          if (fn) {
            if (!byObj[FIELD_DELTA_USER_DB]) byObj[FIELD_DELTA_USER_DB] = [];
            byObj[FIELD_DELTA_USER_DB].push(fn);
          }
          return;
        }
        if (rest.startsWith(FIELD_DELTA_USER_CRM_PREFIX)) {
          const fn = rest.slice(FIELD_DELTA_USER_CRM_PREFIX.length);
          if (fn) {
            if (!byObj[FIELD_DELTA_USER_DB]) byObj[FIELD_DELTA_USER_DB] = [];
            byObj[FIELD_DELTA_USER_DB].push(fn);
          }
          return;
        }
        if (rest.startsWith(FIELD_DELTA_USER_COL_PREFIX)) {
          const col = rest.slice(FIELD_DELTA_USER_COL_PREFIX.length);
          if (col) {
            if (!byObj[FIELD_DELTA_USER_DB]) byObj[FIELD_DELTA_USER_DB] = [];
            byObj[FIELD_DELTA_USER_DB].push(col);
          }
        }
        return;
      }
      const sep = k.indexOf('|||');
      if (sep < 0) return;
      const obj = k.slice(0, sep);
      const fn = k.slice(sep + 3);
      if (!byObj[obj]) byObj[obj] = [];
      byObj[obj].push(fn);
    });
    const items = Object.keys(byObj).map((objectName) => ({ objectName, fieldNames: byObj[objectName] }));
    if (
      items.length === 0 &&
      telecmiSyncSafeColumns.length === 0 &&
      userTableSyncSafeColumns.length === 0
    ) {
      setMigrationError('Nothing in your selection can be migrated.');
      return;
    }
    setMigrateLoading(true);
    setMigrationError('');
    setMigrationSuccess(null);
    try {
      let crmCount = 0;
      const migrateResult = await adminSchemaApi.migrateMigrationFieldDelta(
        companyId,
        migrationSourceSchema,
        items,
        null,
        includeLookup,
        {
          telecmiSyncSafeColumns,
          userTableSyncSafeColumns,
        }
      );
      crmCount = keys.filter((k) => !k.startsWith(telecmiPrefix) && !k.startsWith(userDbPrefix)).length;
      const parts = [];
      const safeSyncErrors = [];
      const summaries = Array.isArray(migrateResult?.summaries) ? migrateResult.summaries : [];
      summaries.forEach((s) => {
        const on = String(s?.objectName || '').toLowerCase();
        if (s.safeFieldSync) {
          if (s.updated && (s.columnsSynced || []).length > 0) {
            if (on === FIELD_DELTA_TELECMI_TABLE) {
              parts.push(`TeleCMI: synced ${(s.columnsSynced || []).join(', ')}`);
            } else if (on === FIELD_DELTA_USER_DB) {
              parts.push(`User table: synced ${(s.columnsSynced || []).join(', ')}`);
            }
          } else if (s.message) {
            safeSyncErrors.push(s.message);
          }
          return;
        }
        if (on === FIELD_DELTA_TELECMI_TABLE) {
          if ((s.columnsAdded || 0) > 0) {
            const names =
              Array.isArray(s.columnsAddedNames) && s.columnsAddedNames.length > 0
                ? `: ${s.columnsAddedNames.join(', ')}`
                : '';
            parts.push(`TeleCMI: added ${s.columnsAdded} column(s) on target${names}`);
          } else if (s.message) {
            parts.push(`TeleCMI: ${s.message}`);
          }
        } else if (on === FIELD_DELTA_USER_DB) {
          const userParts = [];
          if ((s.columnsAdded || 0) > 0) {
            const names =
              Array.isArray(s.columnsAddedNames) && s.columnsAddedNames.length > 0
                ? `: ${s.columnsAddedNames.join(', ')}`
                : '';
            userParts.push(`added ${s.columnsAdded} DB column(s)${names}`);
          }
          if ((s.fieldsInserted || 0) > 0) {
            userParts.push(`added ${s.fieldsInserted} CRM field definition(s)`);
          }
          if ((s.configValuesSynced || 0) > 0) {
            userParts.push(`synced config on ${s.configValuesSynced} field(s)`);
          }
          if (userParts.length > 0) {
            parts.push(`User: ${userParts.join('; ')}`);
          } else if (s.message) {
            parts.push(`User: ${s.message}`);
          }
        }
      });
      if (crmCount > 0) {
        parts.push(`${crmCount} CRM field selection(s) migrated`);
      }
      if (parts.length > 0) {
        setMigrationSuccess({
          message: `${parts.join('; ')}.`,
        });
      }
      if (safeSyncErrors.length > 0) {
        setMigrationError(safeSyncErrors.join('; '));
      }
      const refreshed = await adminSchemaApi.compareMigrationFieldDelta(
        companyId,
        migrationSourceSchema,
        [],
        selectedTable,
        null
      );
      setCompareData(refreshed);
      applySelection({});
    } catch (err) {
      setMigrationError(err?.message || 'Field migration failed');
    } finally {
      setMigrateLoading(false);
    }
  }, [
    companyId,
    migrationSourceSchema,
    selectedTable,
    includeLookup,
    migrationPlan?.targetSchemaExists,
    company?.default_schema_user_id,
    applySelection,
  ]);

  if (!company) {
    return null;
  }

  const crmObjectSummaries =
    Array.isArray(migrationPlan?.fieldMigrationCrmObjects) && migrationPlan.fieldMigrationCrmObjects.length > 0
      ? migrationPlan.fieldMigrationCrmObjects
      : migrationPlan?.alreadyMigratedObjects || [];

  const migratedNames = fieldMigrationObjectsForDisplay(crmObjectSummaries.map((d) => d.objectName));
  const migratedDetailsByName = new Map(
    crmObjectSummaries.map((d) => [String(d.objectName || '').toLowerCase(), d])
  );
  const crmTableOptions = migratedNames
    .map((name) => {
      const d = migratedDetailsByName.get(String(name).toLowerCase());
      const api = d?.objectApiName || name;
      return { value: name, label: labelApiDisplay(d?.objectLabel, api) };
    })
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  const isTelecmiView = selectedTable === FIELD_DELTA_TELECMI_TABLE;
  const isUserDbView = selectedTable === FIELD_DELTA_USER_DB;
  const ts = compareData?.telecmiSetting;
  const ut = compareData?.userTable;
  const planReady = migrationPlan && !migrationPlanLoading;
  const telecmiTableDisplay = labelApiDisplay(TELECMI_SETTING_UI_LABEL, FIELD_DELTA_TELECMI_TABLE);
  const userDbTableDisplay = labelApiDisplay(USER_DB_UI_LABEL, 'user');
  const selectedCrmResult =
    !isTelecmiView && !isUserDbView && selectedTable && compareData?.results
      ? (compareData.results || []).find(
          (r) => String(r.objectName || '').toLowerCase() === String(selectedTable).toLowerCase()
        )
      : null;
  const missingFieldsSectionTitle = isTelecmiView
    ? `TeleCMI drift — ${telecmiTableDisplay}`
    : isUserDbView
      ? `User table drift — ${userDbTableDisplay}`
      : `Missing fields — ${labelApiDisplay(selectedCrmResult?.objectLabel, selectedCrmResult?.objectApiName || selectedTable)}`;

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable centered>
      <Modal.Header closeButton>
        <Modal.Title className="admin-title">Field migration</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="admin-subtitle small mb-3">
          {company.name || 'Company'} — choose a table to load migratable fields (missing CRM definitions,{' '}
          <code className="small">telecmi_setting</code>, or <code className="small">user</code> table columns / User CRM fields and safe value drift). For{' '}
          <code className="small">user</code>, the company&apos;s <strong>Default admin for schema</strong> row is used to compare values; then select rows and use{' '}
          <strong>Migrate selected fields</strong>.
        </p>

        {migrationError && (
          <Alert variant="warning" dismissible onClose={() => setMigrationError('')} className="mb-3">
            {migrationError}
          </Alert>
        )}
        {migrationSuccess && (
          <Alert variant="success" dismissible onClose={() => setMigrationSuccess(null)} className="mb-3">
            {migrationSuccess.message}
          </Alert>
        )}

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Source schema</Form.Label>
              <Form.Select
                value={migrationSourceSchema}
                onChange={(e) => handleSourceChange(e.target.value)}
                disabled={!company.tenantcode}
              >
                <option value="">— Select —</option>
                {migrationSchemas.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          {migrationPlan?.targetSchemaExists && !company?.default_schema_user_id && (
            <Col md={6} className="d-flex align-items-end">
              <p className="small text-warning mb-3 mb-md-0">
                Set <strong>Default admin for schema</strong> on the company page to migrate audit fields.
              </p>
            </Col>
          )}
        </Row>

        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">Table / object</Form.Label>
          <Form.Select value={selectedTable} onChange={handleTableChange} disabled={!migrationSourceSchema}>
            <option value="">— Choose one table —</option>
            <option value={FIELD_DELTA_TELECMI_TABLE}>{telecmiTableDisplay}</option>
            <option value={FIELD_DELTA_USER_DB}>{userDbTableDisplay}</option>
            {crmTableOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
          {!planReady && migrationSourceSchema && (
            <Form.Text className="text-muted small">Loading migration plan…</Form.Text>
          )}
          {planReady && crmTableOptions.length === 0 && (
            <Form.Text className="text-muted small">
              No CRM objects on target yet — you can still compare <code className="small">telecmi_setting</code> or the{' '}
              <code className="small">user</code> table.
            </Form.Text>
          )}
        </Form.Group>

        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <Button
            type="button"
            size="sm"
            variant="outline-primary"
            disabled={compareLoading || !migrationSourceSchema || !selectedTable}
            onClick={runCompare}
          >
            {compareLoading ? 'Refreshing…' : 'Refresh compare'}
          </Button>
          <Form.Check
            type="checkbox"
            id="field-migration-modal-include-lookup"
            label="When migrating lookups, copy related list rows from source"
            checked={includeLookup}
            onChange={(e) => setIncludeLookup(e.target.checked)}
            className="small ms-1"
          />
        </div>

        {isTelecmiView && ts && !ts.error && planReady && (
          <p className="small text-muted mb-2 border-start border-3 ps-2">
            {telecmiTableDisplay}: select checkboxes in the table for missing columns or non-secret value drift, then{' '}
            <strong>Migrate selected fields</strong>.{' '}
            {migrationPlan?.targetSchemaExists && !company?.default_schema_user_id && (
              <span className="text-warning fw-semibold">Set Default admin for schema on the company page.</span>
            )}
          </p>
        )}

        {isTelecmiView && ts?.error && (
          <Alert variant="warning" className="py-2 mb-3">
            {ts.error}
          </Alert>
        )}

        {isUserDbView && ut && !ut.error && planReady && (
          <div className="small text-muted mb-2 border-start border-3 ps-2">
            <div className="fw-semibold text-dark mb-1">{userDbTableDisplay}</div>
            {ut.needsTargetUser && (
              <p className="mb-1">
                Set <strong>Default admin for schema</strong> on the company detail page so the same user id can be compared in source and target.
              </p>
            )}
            {ut.message && !ut.needsTargetUser && <p className="mb-1">{ut.message}</p>}
            {ut.crmObjectError && (
              <p className="mb-1 text-warning">User CRM object: {ut.crmObjectError}</p>
            )}
            <p className="mb-0">
              Each row is one User field. Migration adds the DB column and/or CRM definition on the target as needed, then
              copies non-secret row values when applicable. Select rows below, then <strong>Migrate selected fields</strong>.
            </p>
          </div>
        )}

        {isUserDbView && ut?.error && (
          <Alert variant="warning" className="py-2 mb-3">
            {ut.error}
          </Alert>
        )}

        {selectedTable && (
          <div className="small mt-2">
            {compareLoading && !compareData && (
              <p className="text-muted mb-2">Loading field list…</p>
            )}
            {compareData && (
              <>
                <div className="fw-semibold mb-2">{missingFieldsSectionTitle}</div>
                {(() => {
              const rows = [];
              if (isTelecmiView && ts && !ts.error) {
                (ts.columnsMissingInTarget || []).forEach((col) => {
                  const rowKey = `${FIELD_DELTA_TELECMI_TABLE}|||${FIELD_DELTA_TELECMI_COL_PREFIX}${col}`;
                  rows.push(
                    <tr key={rowKey} className="table-warning">
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={!!selectedFields[rowKey]}
                          onChange={() =>
                            applySelection((prev) => ({
                              ...prev,
                              [rowKey]: !prev[rowKey],
                            }))
                          }
                          aria-label={`Select ${col}`}
                        />
                      </td>
                      <td>{telecmiTableDisplay}</td>
                      <td>{col}</td>
                      <td className="text-muted">Column missing on target DB</td>
                      <td>column</td>
                      <td className="text-muted">Adds column and copies values (except secrets)</td>
                    </tr>
                  );
                });
                (ts.safeFieldDiffs || []).forEach((d) => {
                  const rowKey = `${FIELD_DELTA_TELECMI_TABLE}|||${FIELD_DELTA_TELECMI_SAFE_PREFIX}${d.column}`;
                  const canPick = !!ts.canSyncSafeFields;
                  rows.push(
                    <tr key={rowKey}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          disabled={!canPick}
                          checked={!!selectedFields[rowKey]}
                          onChange={() =>
                            applySelection((prev) => ({
                              ...prev,
                              [rowKey]: !prev[rowKey],
                            }))
                          }
                        />
                      </td>
                      <td>{telecmiTableDisplay}</td>
                      <td>{d.column}</td>
                      <td className="text-break small">
                        {d.sourceValue} → {d.targetValue}
                      </td>
                      <td>telecmi (non-secret)</td>
                      <td className="text-muted">—</td>
                    </tr>
                  );
                });
              } else if (isUserDbView && ut && !ut.error) {
                (ut.migratableFields || []).forEach((f) => {
                  const rowKey = `${FIELD_DELTA_USER_DB}|||${FIELD_DELTA_USER_FIELD_PREFIX}${f.field_name}`;
                  rows.push(
                    <tr key={rowKey} className="table-warning">
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={!!selectedFields[rowKey]}
                          onChange={() =>
                            applySelection((prev) => ({
                              ...prev,
                              [rowKey]: !prev[rowKey],
                            }))
                          }
                          aria-label={`Select ${f.field_name}`}
                        />
                      </td>
                      <td>{userDbTableDisplay}</td>
                      <td>{labelApiDisplay(f.field_label, f.field_name)}</td>
                      <td className="text-muted">{userMigratableFieldDetail(f)}</td>
                      <td>{f.data_type || (f.needsColumn ? 'column' : '—')}</td>
                      <td className="text-muted">
                        {f.isLookup ? `Lookup → ${f.reference_object || '—'}` : userMigratableFieldActionLabel(f)}
                      </td>
                    </tr>
                  );
                });
                (ut.safeFieldDiffs || []).forEach((d) => {
                  const rowKey = `${FIELD_DELTA_USER_DB}|||${FIELD_DELTA_USER_SAFE_PREFIX}${d.column}`;
                  const canPick = !!ut.canSyncSafeFields;
                  rows.push(
                    <tr key={rowKey}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          disabled={!canPick}
                          checked={!!selectedFields[rowKey]}
                          onChange={() =>
                            applySelection((prev) => ({
                              ...prev,
                              [rowKey]: !prev[rowKey],
                            }))
                          }
                        />
                      </td>
                      <td>{userDbTableDisplay}</td>
                      <td>{d.column}</td>
                      <td className="text-break small">
                        {d.sourceValue} → {d.targetValue}
                      </td>
                      <td>user row (non-secret)</td>
                      <td className="text-muted">—</td>
                    </tr>
                  );
                });
              } else if (!isTelecmiView && !isUserDbView && selectedTable) {
                const stLower = selectedTable.toLowerCase();
                (compareData.results || []).forEach((r) => {
                  if (String(r.objectName || '').toLowerCase() !== stLower) return;
                  if (r.error) {
                    const objDisp = labelApiDisplay(r.objectLabel, r.objectApiName || r.objectName);
                    rows.push(
                      <tr key={`err-${r.objectName}`}>
                        <td colSpan={6}>
                          <Alert variant="warning" className="py-2 mb-0">
                            <strong>{objDisp}:</strong> {r.error}
                          </Alert>
                        </td>
                      </tr>
                    );
                    return;
                  }
                  if (!Array.isArray(r.missingFields)) return;
                  const objectDisp = labelApiDisplay(r.objectLabel, r.objectApiName || r.objectName);
                  r.missingFields.forEach((f) => {
                    const rowKey = `${r.objectName}|||${f.field_name}`;
                    rows.push(
                      <tr key={rowKey}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={!!selectedFields[rowKey]}
                            onChange={() =>
                              applySelection((prev) => ({
                                ...prev,
                                [rowKey]: !prev[rowKey],
                              }))
                            }
                          />
                        </td>
                        <td>{objectDisp}</td>
                        <td>{labelApiDisplay(f.field_label, f.field_name)}</td>
                        <td className="text-muted">—</td>
                        <td>{f.data_type}</td>
                        <td>{f.isLookup ? `Lookup → ${f.reference_object || '—'}` : '—'}</td>
                      </tr>
                    );
                  });
                });
              }

              if (rows.length === 0) {
                return (
                  <p className="text-muted mb-0">
                    {compareLoading
                      ? 'Loading…'
                      : selectedTable
                        ? 'No migratable fields for this table with current settings.'
                        : 'Choose a table to load migratable fields.'}
                  </p>
                );
              }

              const allKeys = [];
              if (isTelecmiView && ts && !ts.error) {
                (ts.columnsMissingInTarget || []).forEach((col) => {
                  allKeys.push(`${FIELD_DELTA_TELECMI_TABLE}|||${FIELD_DELTA_TELECMI_COL_PREFIX}${col}`);
                });
                if (ts.canSyncSafeFields) {
                  (ts.safeFieldDiffs || []).forEach((d) => {
                    allKeys.push(`${FIELD_DELTA_TELECMI_TABLE}|||${FIELD_DELTA_TELECMI_SAFE_PREFIX}${d.column}`);
                  });
                }
              } else if (isUserDbView && ut && !ut.error) {
                (ut.migratableFields || []).forEach((f) => {
                  allKeys.push(`${FIELD_DELTA_USER_DB}|||${FIELD_DELTA_USER_FIELD_PREFIX}${f.field_name}`);
                });
                if (ut.canSyncSafeFields) {
                  (ut.safeFieldDiffs || []).forEach((d) => {
                    allKeys.push(`${FIELD_DELTA_USER_DB}|||${FIELD_DELTA_USER_SAFE_PREFIX}${d.column}`);
                  });
                }
              } else if (!isTelecmiView && !isUserDbView && selectedTable) {
                const stLower = selectedTable.toLowerCase();
                (compareData.results || []).forEach((r) => {
                  if (r.error || String(r.objectName || '').toLowerCase() !== stLower) return;
                  if (!Array.isArray(r.missingFields)) return;
                  r.missingFields.forEach((f) => {
                    allKeys.push(`${r.objectName}|||${f.field_name}`);
                  });
                });
              }

              return (
                <>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      type="button"
                      onClick={() => {
                        applySelection((prev) => {
                          const next = { ...prev };
                          allKeys.forEach((k) => {
                            next[k] = true;
                          });
                          return next;
                        });
                      }}
                    >
                      Select all migratable
                    </Button>
                    <Button size="sm" variant="outline-secondary" type="button" onClick={() => applySelection({})}>
                      Clear selection
                    </Button>
                  </div>
                  <Table size="sm" bordered responsive className="mb-2">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }} />
                        <th>Object (label &amp; API)</th>
                        <th>Field (label &amp; API)</th>
                        <th>Detail / values</th>
                        <th>Type</th>
                        <th>Relationship / notes</th>
                      </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                  </Table>
                  <Button
                    type="button"
                    size="sm"
                    className="admin-primary-btn"
                    disabled={migrateLoading || !selectedTable}
                    onClick={handleMigrate}
                  >
                    {migrateLoading ? 'Migrating…' : 'Migrate selected fields'}
                  </Button>
                  {migrationPlan?.targetSchemaExists && !company?.default_schema_user_id && (
                    <span className="small text-warning ms-2 fw-semibold">Set Default admin for schema on the company page.</span>
                  )}
                </>
              );
                })()}
              </>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
